import { HttpContextToken, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, TimeoutError, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { HttpErrorModel } from './http-error.interceptor';

/** How long one attempt of this request may wait for the server, in ms; 0 means no limit. */
export const REQUEST_TIMEOUT_MS = new HttpContextToken<number | undefined>(() => undefined);

/** A healthy call answers in a second or two, so this long means the connection is gone. */
export const DEFAULT_TIMEOUT_MS = 30_000;
/** Work that can legitimately take a while: reports, exports, uploads, sends and payments. */
export const SLOW_TIMEOUT_MS = 120_000;

/** Paths whose requests can run long whatever the method. */
const SLOW_PATHS = ['/reports', '/export', '/download'];
/**
 * Paths whose writes can run long: sends and payments. A charge that times out
 * may still go through, so these get the longest wait before saying so. Their
 * reads (a sales list, a send's progress) are ordinary.
 */
const SLOW_WRITE_PATHS = ['/bulkemail', '/bulktext', '/stripe', '/sales', '/cardterminal', '/square', '/paypal'];

export const TIMEOUT_MESSAGE =
  "This is taking too long. Check your connection. If you were saving or paying, check whether it went through before trying again.";

/** Same shape HttpErrorInterceptor gives every other failure, so callers need nothing new. */
export interface TimeoutErrorModel extends HttpErrorModel { timedOut: true; }

/**
 * Bounds every request, so a connection that accepts a request and then goes
 * silent (captive wifi, a cell handoff) fails with a message instead of
 * spinning forever. Applied per attempt: SalonmonsterHttpClient's retry
 * resubscribes, and each attempt gets its own limit.
 *
 * The error has status 0, like a dropped connection: reads and safe writes
 * retry, writes that must not repeat do not, and a billed send is kept as
 * unconfirmed because its outcome is unknown.
 */
export class TimeoutInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const ms = timeoutFor(request);
    if (!ms) return next.handle(request);
    return next.handle(request).pipe(
      timeout(ms),
      catchError(error => throwError(() => error instanceof TimeoutError
        ? ({ status: 0, message: TIMEOUT_MESSAGE, timedOut: true } as TimeoutErrorModel)
        : error))
    );
  }
}

export function timeoutFor(request: HttpRequest<any>): number {
  const set = request.context.get(REQUEST_TIMEOUT_MS);
  if (set !== undefined) return set;
  const path = request.url.split('?')[0].toLowerCase();
  const slow = request.body instanceof FormData || request.body instanceof Blob || request.reportProgress
    || request.responseType === 'blob' || request.responseType === 'arraybuffer'
    || SLOW_PATHS.some(part => path.includes(part))
    || (request.method !== 'GET' && SLOW_WRITE_PATHS.some(part => path.includes(part)));
  return slow ? SLOW_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;
}
